// ChooseAccess.js
import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Linking,
  Platform,
  ActivityIndicator,
  Animated,
} from 'react-native';

// Helper: format ms to HH:MM:SS
const formatHMS = (ms) => {
  if (ms <= 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const SUBSCRIBE_URL = 'https://collabbayadmin.com/subscribe';
const HOMEPAGE_URL = '/BusinessHomepage';

const ChooseAccess = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [createdAt, setCreatedAt] = useState(null); // JS Date
  const [remainingMs, setRemainingMs] = useState(null);
  const intervalRef = useRef(null);

  // Top-right exit button (fades in after 2s)
  const [showExit, setShowExit] = useState(false);
  const exitOpacity = useRef(new Animated.Value(0)).current;

  // Fetch session to get CreatedAt
  useEffect(() => {
    let cancelled = false;

    const fetchSession = async () => {
      try {
        const res = await fetch('/GetSessionData.php', {
          method: 'GET',
          credentials: 'include', // critical for PHP session
          headers: { Accept: 'application/json' },
        });
        const data = await res.json();
        // Expect data.CreatedAt (e.g., "2025-09-18 13:45:02")
        if (!cancelled && data && data.CreatedAt) {
          // Ensure cross-platform parse: replace space with 'T'
          const isoLike = String(data.CreatedAt).replace(' ', 'T');
          const created = new Date(isoLike);
          if (!isNaN(created.getTime())) {
            setCreatedAt(created);
          }
        }
      } catch (e) {
        // silently ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // Compute deadline (CreatedAt + 24 hours) and start ticking
  const deadline = useMemo(() => {
    if (!createdAt) return null;
    return new Date(createdAt.getTime() + 24 * 60 * 60 * 1000); // +24 hours
  }, [createdAt]);

  useEffect(() => {
    if (!deadline) return;

    const tick = () => {
      const ms = deadline.getTime() - Date.now();
      setRemainingMs(ms > 0 ? ms : 0);
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [deadline]);

  // Show exit after 2s + fade it in
  useEffect(() => {
    const t = setTimeout(() => {
      setShowExit(true);
      Animated.timing(exitOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }, 2000);
    return () => clearTimeout(t);
  }, [exitOpacity]);

  const discountActive = remainingMs !== null && remainingMs > 0;

  // Navigation helpers
  const goSubscribe = () => {
    // Prefer hard redirect on web (same tab) to avoid SPA routing issues
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.href = SUBSCRIBE_URL;
      return;
    }
    // Fallback to deep link / in-app browser on native
    Linking.openURL(SUBSCRIBE_URL);
  };

  const goHome = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // keep your current site routing
      window.location.href = HOMEPAGE_URL;
      return;
    }
    if (navigation && typeof navigation.navigate === 'function') {
      navigation.navigate('BusinessHomepage');
      return;
    }
    Linking.openURL(HOMEPAGE_URL);
  };

  return (
    <SafeAreaView style={styles.safe}>
      {/* Top-right X (after 2s) */}
      {showExit && (
        <Animated.View style={[styles.exitWrap, { opacity: exitOpacity }]}>
          <TouchableOpacity onPress={goHome} accessibilityLabel="Close and go to homepage">
            <Text style={styles.exitX}>×</Text>
          </TouchableOpacity>
        </Animated.View>
      )}

      <View style={styles.wrap}>
        <Text style={styles.h1}>Pick how you want to start</Text>
        <Text style={styles.sub}>
          We’ll list your offer either way — choose what works for you.
        </Text>

        {loading && (
          <View style={styles.loadingRow}>
            <ActivityIndicator />
            <Text style={styles.loadingText}>Loading your welcome offer…</Text>
          </View>
        )}

        <View style={styles.grid}>
          {/* Left card: Unlock full access */}
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Unlock full access</Text>

            <View style={styles.priceRow}>
              {discountActive ? (
                <>
                  <Text style={styles.priceNow}>£63</Text>
                  <Text style={styles.priceWas}>£70</Text>
                </>
              ) : (
                <Text style={styles.priceNow}>£70</Text>
              )}
            </View>

            {discountActive ? (
              <View style={styles.timerPill}>
                <Text style={styles.timerText}>
                  Offer expires in {formatHMS(remainingMs)}
                </Text>
              </View>
            ) : (
              <View style={[styles.timerPill, { backgroundColor: '#F3F3F3' }]}>
                <Text style={[styles.timerText, { color: '#444' }]}>
                  Welcome discount ended
                </Text>
              </View>
            )}

            <View style={styles.bullets}>
              <Text style={styles.bullet}>• + Offer boost (higher visibility)</Text>
              <Text style={styles.bullet}>• Discounted welcome offer</Text>
              <Text style={styles.bullet}>• Unlock all requests & metrics</Text>
            </View>

            <TouchableOpacity onPress={goSubscribe} style={[styles.cta, styles.ctaYellow]}>
              <Text style={[styles.ctaText, styles.ctaTextDark]}>
                {discountActive ? 'Unlock now for £63' : 'Unlock now for £70'}
              </Text>
            </TouchableOpacity>

            <Text style={styles.fine}>
              Your welcome window is 24 hours from signup.
            </Text>
          </View>

          {/* Right card: Try it first */}
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Try it first</Text>

            <View style={[styles.timerPill, { backgroundColor: '#F3F3F3', marginTop: 8 }]}>
              <Text style={[styles.timerText, { color: '#444' }]}>
                No commitment required
              </Text>
            </View>

            <View style={styles.bullets} accessibilityRole="text">
              <Text style={styles.bullet}>
                • Not ready to fully subscribe? No worries.
              </Text>
              <Text style={styles.bullet}>
                • We’ll still list your offer and you can collab first.
              </Text>
              <Text style={styles.bullet}>
                • Decide later when you’re ready to unlock everything.
              </Text>
            </View>

            <TouchableOpacity onPress={goHome} style={[styles.cta, styles.ctaYellow]}>
              <Text style={[styles.ctaText, styles.ctaTextDark]}>
                Continue without subscribing
              </Text>
            </TouchableOpacity>

            <Text style={styles.fine}>
              You can upgrade anytime from your dashboard.
            </Text>
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
};

const THEME = {
  bg: '#FFFFFF',
  ink: '#0B0B0C',
  inkMut: '#3A3A3C',
  line: '#E9EAEB',
  accent: '#FDF05D', // Collabbay yellow
  accentInk: '#111',
};

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: THEME.bg,
    alignItems: 'center',
  },
  exitWrap: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 10,
    pointerEvents: 'auto',
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: THEME.line,
  },
  exitX: {
    fontSize: 26,
    lineHeight: 28,
    fontWeight: '800',
    color: THEME.ink,
    textAlign: 'center',
    minWidth: 20,
  },
  wrap: {
    width: '100%',
    maxWidth: 960,
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  h1: {
    fontSize: 24,
    fontWeight: '700',
    color: THEME.ink,
    marginBottom: 6,
  },
  sub: {
    fontSize: 14,
    color: THEME.inkMut,
    marginBottom: 20,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  loadingText: {
    color: THEME.inkMut,
  },
  grid: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  card: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 280,
    backgroundColor: '#FFF',
    borderWidth: 1,
    borderColor: THEME.line,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOpacity: Platform.OS === 'web' ? 0.06 : 0.12,
    shadowOffset: { width: 0, height: 6 },
    shadowRadius: 12,
    elevation: 2,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: THEME.ink,
    marginBottom: 8,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    marginTop: 4,
    marginBottom: 8,
  },
  priceNow: {
    fontSize: 28,
    fontWeight: '800',
    color: THEME.ink,
  },
  priceWas: {
    fontSize: 16,
    color: '#8A8A8E',
    textDecorationLine: 'line-through',
    marginBottom: 4,
  },
  timerPill: {
    alignSelf: 'flex-start',
    backgroundColor: THEME.accent,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginTop: 4,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E6E1A6',
  },
  timerText: {
    fontSize: 12,
    fontWeight: '700',
    color: THEME.accentInk,
    letterSpacing: 0.2,
  },
  bullets: {
    marginTop: 4,
    marginBottom: 16,
  },
  bullet: {
    fontSize: 14,
    color: THEME.ink,
    marginBottom: 6,
  },
  cta: {
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    marginTop: 4,
  },
  // Yellow buttons for BOTH CTAs
  ctaYellow: {
    backgroundColor: THEME.accent,
    borderColor: THEME.accent,
  },
  ctaText: {
    fontWeight: '700',
    fontSize: 15,
  },
  ctaTextDark: {
    color: THEME.ink, // black text on yellow
  },
  fine: {
    marginTop: 10,
    fontSize: 12,
    color: THEME.inkMut,
  },
});

export default ChooseAccess;
